import React, { useRef, useCallback, useEffect, useState } from "react";
import { Box, Typography, Container } from "@mui/material";
import InstagramIcon from "@mui/icons-material/Instagram";

// ─── Data ────────────────────────────────────────────────────────────────────
const posts: { id: number; shortcode: string; type: "reel" | "post"; variant?: "video" }[] = [
  { id: 1, shortcode: "DMhsJyFMYOQ", type: "reel" },
  { id: 2, shortcode: "DM1Idi3NW0M", type: "reel" },
  { id: 3, shortcode: "DN-EO-GjDA7", type: "reel" },
  { id: 4, shortcode: "DXoHloQjGce", type: "reel" },
  { id: 5, shortcode: "DWkzTZRjET6", type: "reel", variant: "video" },
];

const INSTAGRAM_URL = "https://www.instagram.com/drpaw.lb";
const HANDLE = "@drpawleb";
// ─── Helpers ─────────────────────────────────────────────────────────────────
const getEmbedUrl = (shortcode: string, type: "reel" | "post") =>
  type === "reel"
    ? `https://www.instagram.com/reel/${shortcode}/embed/?cr=1&v=14`
    : `https://www.instagram.com/p/${shortcode}/embed/?cr=1&v=14`;

const getIframeStyle = (type: "reel" | "post", variant?: "video"): React.CSSProperties => {
  if (type === "reel") {
    return { width: "170%", height: "220%", marginTop: "-130px", marginLeft: "-35%" };
  }
  if (variant === "video") {
    return { width: "222%", height: "150%", marginTop: "-50px", marginLeft: "-10%" };
  }
  return { width: "130%", height: "130%", marginTop: "-70px", marginLeft: "-15%" };
};

// ─── PostCard ─────────────────────────────────────────────────────────────────
const PostCard: React.FC<{ post: typeof posts[0] }> = ({ post }) => {
  const [hovered, setHovered] = useState(false);
  const postUrl = `https://www.instagram.com/${post.type === "reel" ? "reel" : "p"}/${post.shortcode}/`;

  return (
    <Box
      component="a"
      href={postUrl}
      target="_blank"
      rel="noopener noreferrer"
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      sx={{
        flex: { xs: "0 0 78vw", sm: "1 1 0" },
        aspectRatio: "4/5",
        overflow: "hidden",
        position: "relative",
        backgroundColor: "#ede8e3",
        borderRight: "1px solid rgba(255,255,255,0.6)",
        scrollSnapAlign: "start",
        display: "block",
        textDecoration: "none",
        cursor: "pointer",
        "&:last-child": { borderRight: "none" },
      }}
    >
      <Box sx={{ position: "absolute", inset: 0, overflow: "hidden" }}>
        <iframe
          src={getEmbedUrl(post.shortcode, post.type)}
          style={{
            position: "absolute",
            border: "none",
            pointerEvents: "none",
            ...getIframeStyle(post.type, post.variant),
          }}
          scrolling="no"
          allowTransparency
          allow="autoplay; clipboard-write; encrypted-media; picture-in-picture; web-share"
          title={`Instagram post ${post.shortcode}`}
        />
      </Box>

      {/* Hover overlay */}
      <Box
        sx={{
          position: "absolute",
          inset: 0,
          backgroundColor: "rgba(43,34,29,0.28)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          opacity: hovered ? 1 : 0,
          transition: "opacity 0.3s ease",
        }}
      >
        <Box
          sx={{
            width: 52,
            height: 52,
            borderRadius: "50%",
            backgroundColor: "#fff",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <InstagramIcon sx={{ fontSize: "1.6rem", color: "#2B221D" }} />
        </Box>
      </Box>
    </Box>
  );
};

// ─── InstaSection ─────────────────────────────────────────────────────────────
const InstaSection: React.FC = () => {
  const stripRef = useRef<HTMLDivElement>(null);
  const [progress, setProgress] = useState(0);

  const updateProgress = useCallback(() => {
    const el = stripRef.current;
    if (!el) return;
    const max = el.scrollWidth - el.clientWidth;
    setProgress(max > 0 ? (el.scrollLeft / max) * 100 : 0);
  }, []);

  useEffect(() => {
    const el = stripRef.current;
    if (!el) return;
    el.addEventListener("scroll", updateProgress, { passive: true });
    window.addEventListener("resize", updateProgress);
    return () => {
      el.removeEventListener("scroll", updateProgress);
      window.removeEventListener("resize", updateProgress);
    };
  }, [updateProgress]);

  return (
    <Box sx={{ backgroundColor: "#fff", py: { xs: 6, md: 8 } }}>
      <Container maxWidth="lg">
        {/* Title */}
        <Typography
          sx={{
            textAlign: "center",
            fontFamily: "Georgia, serif",
            fontSize: { xs: "1.9rem", md: "2.6rem" },
            fontWeight: 700,
            color: "#2B221D",
            mb: { xs: 4, md: 5 },
          }}
        >
          Follow Us{" "}
        </Typography>
      </Container>

      {/* Post strip */}
      <Box
        ref={stripRef}
        sx={{
          display: "flex",
          flexDirection: "row",
          overflowX: { xs: "scroll", sm: "visible" },
          scrollSnapType: { xs: "x mandatory", sm: "none" },
          "&::-webkit-scrollbar": { display: "none" },
          scrollbarWidth: "none",
          // desktop: grid
          "@media (min-width: 601px)": {
            display: "grid",
            gridTemplateColumns: "repeat(3, 1fr)",
          },
          "@media (min-width: 901px)": {
            gridTemplateColumns: `repeat(${posts.length}, 1fr)`,
          },
        }}
      >
        {posts.map((post) => (
          <PostCard key={post.id} post={post} />
        ))}
      </Box>

      {/* Mobile scroll progress bar */}
      <Box
        sx={{
          display: { xs: "block", sm: "none" },
          px: 3,
          mt: 2,
        }}
      >
        <Box sx={{ backgroundColor: "#ece3da", borderRadius: 999, height: "3px", overflow: "hidden" }}>
          <Box
            sx={{
              height: "100%",
              borderRadius: 999,
              backgroundColor: "#D89A7A",
              width: `${progress}%`,
              transition: "width 0.1s linear",
            }}
          />
        </Box>
      </Box>

      {/* CTA button */}
      <Box sx={{ display: "flex", justifyContent: "center", mt: { xs: 4, md: 5 } }}>
        <Box
          component="a"
          href={INSTAGRAM_URL}
          target="_blank"
          rel="noopener noreferrer"
          sx={{
            display: "inline-flex",
            alignItems: "center",
            gap: 1,
            px: 3.5,
            py: 1.3,
            borderRadius: 999,
            border: "1.5px solid #c8b49a",
            color: "#2B221D",
            textDecoration: "none",
            fontWeight: 600,
            fontSize: "0.95rem",
            transition: "background 0.25s ease, color 0.25s ease",
            "&:hover": {
              backgroundColor: "#2B221D",
              color: "#fff",
              "& svg": { color: "#fff" },
            },
          }}
        >
          <InstagramIcon sx={{ fontSize: "1.2rem", transition: "color 0.25s ease" }} />
          {HANDLE}
        </Box>
      </Box>
    </Box>
  );
};

export default InstaSection;
